import { useState } from 'react';
import { Grid, TextField, MenuItem } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import { createAutojob2, CreateAutojob2Request } from 'src/shared/services/autojobService';
import { toast } from 'react-toastify';

type CreateAutojobModalProps = {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
};

const assemblyNameOptions = [{ value: 'CreateAndSendAutogiroInvoices', label: 'CreateAndSendAutogiroInvoices' }];

const scheduleTypeOptions = [
  { value: 0, labelKey: 'settings:autojobs.scheduleTypes.once' },
  { value: 1, labelKey: 'settings:autojobs.scheduleTypes.minute' },
  { value: 2, labelKey: 'settings:autojobs.scheduleTypes.hourly' },
  { value: 3, labelKey: 'settings:autojobs.scheduleTypes.daily' },
  { value: 4, labelKey: 'settings:autojobs.scheduleTypes.weekly' },
  { value: 5, labelKey: 'settings:autojobs.scheduleTypes.monthly' },
  { value: 6, labelKey: 'settings:autojobs.scheduleTypes.yearly' },
];

const defaultForm: CreateAutojob2Request = {
  taskName: '',
  assemblyName: '',
  scheduleType: 0,
  increaseNum: 0,
  startTimeStamp: new Date(Date.now() + 2 * 60 * 1000).toISOString().slice(0, 16),
  custom1: '',
  custom2: '',
  custom3: '',
  custom4: '',
  custom5: '',
  custom6: '',
  custom7: '',
  custom8: '',
  custom9: '',
  custom10: '',
  custom11: '',
  custom12: '',
};

export const CreateAutojobModal = (props: CreateAutojobModalProps) => {
  const { open, onClose, onSaved } = props;
  const { t } = useTranslation(['settings', 'common']);
  const [form, setForm] = useState<CreateAutojob2Request>({ ...defaultForm });
  const [saving, setSaving] = useState(false);

  const updateField = (field: keyof CreateAutojob2Request, value: string | number) => {
    setForm((prev) => {
      const updated = { ...prev, [field]: value };

      // Förifyll fält vid val av autogiro
      if (field === 'assemblyName' && value === 'CreateAndSendAutogiroInvoices') {
        updated.taskName = updated.taskName || 'Autogiroavisering medlemsavgift';
        updated.scheduleType = 5;
        updated.increaseNum = 1;
        updated.custom1 = 'CreateInvoices';
      }

      return updated;
    });
  };

  const handleSave = async () => {
    if (!form.taskName || !form.assemblyName) return;
    setSaving(true);
    try {
      await createAutojob2(form);
      toast.success(<>{t('common:statusMessages.saveChangesSuccess')}</>);
      setForm({ ...defaultForm });
      onSaved();
    } catch {
      toast.error(<>{t('common:statusMessages.saveChangesError')}</>);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setForm({ ...defaultForm });
    onClose();
  };

  const customFields = Array.from({ length: 12 }, (_, i) => i + 1);

  return (
    <GenericDialog
      open={open}
      dialogTitle={t('settings:autojobs.createModal.title')}
      maxWidth='sm'
      onClose={handleClose}
      onSave={handleSave}
      saveLabel='common:button.save'
      abortLabel='common:button.cancel'
      dialogContent={
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label={t('settings:autojobs.name')}
              value={form.taskName}
              onChange={(e) => updateField('taskName', e.target.value)}
              fullWidth
              size='small'
              required
              disabled={saving}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label='Assembly'
              value={form.assemblyName}
              onChange={(e) => updateField('assemblyName', e.target.value)}
              fullWidth
              size='small'
              required
              select
              disabled={saving}
            >
              {assemblyNameOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField
              label={t('settings:autojobs.scheduleType')}
              value={form.scheduleType}
              onChange={(e) => updateField('scheduleType', Number(e.target.value))}
              fullWidth
              size='small'
              select
              disabled={saving}
            >
              {scheduleTypeOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {t(opt.labelKey)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField
              label={t('dashboard:autojobs.interval')}
              value={form.increaseNum}
              onChange={(e) => updateField('increaseNum', Number(e.target.value))}
              fullWidth
              size='small'
              type='number'
              disabled={saving}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label={t('settings:autojobs.createModal.startTime')}
              value={form.startTimeStamp}
              onChange={(e) => updateField('startTimeStamp', e.target.value)}
              fullWidth
              size='small'
              type='datetime-local'
              InputLabelProps={{ shrink: true }}
              disabled={saving}
            />
          </Grid>
          {customFields.map((num) => {
            const key = `custom${num}` as keyof CreateAutojob2Request;
            return (
              <Grid item xs={6} key={key}>
                <TextField
                  label={`Custom ${num}`}
                  value={form[key]}
                  onChange={(e) => updateField(key, e.target.value)}
                  fullWidth
                  size='small'
                  disabled={saving}
                />
              </Grid>
            );
          })}
        </Grid>
      }
    />
  );
};
